import { DialogContent, Grid, Autocomplete, Chip, Checkbox } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";

import MDDialog from "components/MDDialog";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import { useDispatch } from "react-redux";

import {
  createDynamicSpace,
  updateDynamicSpace,
  getDynamicSpaces,
} from "../../../store/slice/dynamicSpace/dynamicSpaceSlice";

const daysList = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/* ---------------- VALIDATION ---------------- */

const validationSchema = Yup.object().shape({
  days: Yup.array()
    .min(1, "Select at least one day")
    .required("Days are required"),

  startTime: Yup.string().required("Start time is required"),

  endTime: Yup.string()
    .required("End time is required")
    .test("is-greater", "End time must be after start time", function (value) {
      const { startTime } = this.parent;
      if (!startTime || !value) return true;

      const toMinutes = (time) => {
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
      };

      return toMinutes(value) > toMinutes(startTime);
    }),

  spaces: Yup.number()
    .typeError("Spaces must be a number")
    .required("Spaces are required")
    .min(1, "Spaces must be at least 1"),
});

const ALL_DAYS = "Select All";

const dayOptions = [ALL_DAYS, ...daysList];

const AddDynamicSpace = ({ open, onClose, place, editData, selectedDay }) => {
  const dispatch = useDispatch();

  const isEdit = Boolean(editData);

  return (
    <MDDialog
      dialogTitle={isEdit ? "Edit Dynamic Space" : "Add Dynamic Space"}
      open={open}
      dialogClose={onClose}
      closeIcon
      maxWidth="sm"
    >
      <DialogContent>
        <Formik
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={{
            days: editData ? [editData.day] : [],
            startTime: editData?.startTime || "",
            endTime: editData?.endTime || "",
            spaces: editData?.spaces || "",
          }}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            try {
              const payload = {
                placeId: place?._id,
                days: values.days,
                startTime: values.startTime,
                endTime: values.endTime,
                spaces: Number(values.spaces),
                tz: place?.timeZoneId,
                isFromDynamicSpace: true,
              };

              if (isEdit) {
                await dispatch(
                  updateDynamicSpace({
                    spaceId: editData._id,
                    ...payload,
                  }),
                ).unwrap();
              } else {
                await dispatch(createDynamicSpace(payload)).unwrap();
              }

              dispatch(
                getDynamicSpaces({
                  placeId: place._id,
                  day: selectedDay,
                }),
              );

              onClose();
            } catch (err) {
              setStatus(err?.message || "Something went wrong");
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {(props) => (
            <form onSubmit={props.handleSubmit}>
              <Grid container spacing={2}>
                {/* DAYS */}

                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={dayOptions}
                    value={props.values.days}
                    onChange={(e, value) => {
                      if (value.includes(ALL_DAYS)) {
                        const allSelected =
                          props.values.days.length === daysList.length;

                        props.setFieldValue(
                          "days",
                          allSelected ? [] : daysList,
                        );
                        return;
                      }

                      props.setFieldValue(
                        "days",
                        value.filter((item) => item !== ALL_DAYS),
                      );
                    }}
                    onBlur={() => props.setFieldTouched("days", true)}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderOption={(optionProps, option) => {
                      const isAllSelected =
                        props.values.days.length === daysList.length;

                      const checked =
                        option === ALL_DAYS
                          ? isAllSelected
                          : props.values.days.includes(option);

                      return (
                        <li {...optionProps}>
                          <Checkbox
                            checked={checked}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          {option}
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          label={option}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Select Days"
                        error={props.touched.days && Boolean(props.errors.days)}
                        helperText={props.touched.days && props.errors.days}
                      />
                    )}
                  />
                </Grid>

                {/* START TIME */}

                <Grid item xs={6}>
                  <MDInput
                    type="time"
                    label="Start Time"
                    fullWidth
                    value={props.values.startTime}
                    onChange={(e) =>
                      props.setFieldValue("startTime", e.target.value)
                    }
                    onBlur={props.handleBlur}
                    name="startTime"
                    error={
                      props.touched.startTime && Boolean(props.errors.startTime)
                    }
                    helperText={
                      props.touched.startTime && props.errors.startTime
                    }
                  />
                </Grid>

                {/* END TIME */}

                <Grid item xs={6}>
                  <MDInput
                    type="time"
                    label="End Time"
                    fullWidth
                    value={props.values.endTime}
                    onChange={(e) =>
                      props.setFieldValue("endTime", e.target.value)
                    }
                    onBlur={props.handleBlur}
                    name="endTime"
                    error={
                      props.touched.endTime && Boolean(props.errors.endTime)
                    }
                    helperText={props.touched.endTime && props.errors.endTime}
                  />
                </Grid>

                {/* SPACES */}

                <Grid item xs={12}>
                  <MDInput
                    label="Spaces"
                    type="number"
                    fullWidth
                    name="spaces"
                    value={props.values.spaces}
                    onChange={(e) =>
                      props.setFieldValue("spaces", e.target.value)
                    }
                    onBlur={props.handleBlur}
                    error={props.touched.spaces && Boolean(props.errors.spaces)}
                    helperText={props.touched.spaces && props.errors.spaces}
                  />
                </Grid>

                {/* API ERROR */}

                {props.status && (
                  <Grid item xs={12}>
                    <p style={{ color: "red", fontSize: 13 }}>{props.status}</p>
                  </Grid>
                )}

                {/* SUBMIT */}

                <Grid item xs={12} textAlign="right">
                  <MDButton
                    type="submit"
                    variant="contained"
                    color="info"
                    disabled={props.isSubmitting}
                  >
                    {isEdit ? "Update" : "Create"}
                  </MDButton>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </DialogContent>
    </MDDialog>
  );
};

export default AddDynamicSpace;
